import { OFFICES } from "@/content/locations";
import { presenceFor } from "./presence";

// Typed JSON-LD builders. Keep schema-blocks consistent and link
// everything back to the Organization via @id so search engines and
// LLMs can stitch a single entity graph.

import {
  AWARDS,
  BASE_URL,
  BRAND,
  FENNY_PROFILES,
  LOCALE,
  MEMBERSHIPS,
  ORG_ID,
  ORG_NUMBER,
  VAT_ID,
  organizationIdentifiers,
  PARENT_PROFILES,
  PARENT_LEGAL_NAME,
  PARENT_ORG_NUMBER,
  WEBSITE_ID,
  abs,
  placeProfileUrls,
  wikidataUrl,
} from "./constants";
import { CUSTOMER_REVIEWS, aggregateRatingValue } from "@/content/reviews";
import { COMPANY_FACTS } from "@/content/company-facts";

/**
 * NAP-uppgifter (namn, adress, telefon) som styrs från Admin →
 * Företagsuppgifter. Skickas in i organizationGraph() från rot-loadern;
 * saknas de faller vi tillbaka på COMPANY_FACTS.
 */
export interface BusinessNap {
  phone?: string;
  email?: string;
  streetAddress?: string;
  postalCode?: string;
  city?: string;
  region?: string;
  country?: string;
  openingHours?: string;
  sameAs?: string[];
}


// Citable, ärliga claims om Fenny — driver AEO i ChatGPT/Perplexity.
// Justera siffrorna när du har faktiska tal; håll dem konsistenta överallt.
export const FENNY_FACTS = {
  foundedYear: 2018,
  firstClipPromiseDays: 14,
  monthlyOutputRange: "4–20 filmer per månad",
  priceRangeIndicator: "€€€",
  notableClients: [
    "Garageportexperten",
    "Bolt",
    "Huawei",
    "Renova",
    "Wimeck",
  ],
} as const;

export const organizationGraph = (nap?: BusinessNap) => {
  const fallback = COMPANY_FACTS.postalAddress;
  const street = nap?.streetAddress?.trim() || fallback.streetAddress;
  const postalCode = nap?.postalCode?.trim() || fallback.postalCode;
  const locality = nap?.city?.trim() || fallback.addressLocality;
  const region = nap?.region?.trim() || fallback.addressRegion;
  const country = nap?.country?.trim() || fallback.addressCountry;
  const telephone = nap?.phone?.trim() || COMPANY_FACTS.phone;
  const email = nap?.email?.trim() || COMPANY_FACTS.email;
  const postalAddress = {
    "@type": "PostalAddress",
    ...(street && { streetAddress: street }),
    addressLocality: locality,
    ...(postalCode && { postalCode }),
    ...(region && { addressRegion: region }),
    addressCountry: country,
  };
  const extraSameAs = (nap?.sameAs ?? []).filter(Boolean);

  return {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["Organization", "ProfessionalService", "LocalBusiness"],
      "@id": ORG_ID,
      name: BRAND,
      url: BASE_URL,
      logo: { "@type": "ImageObject", url: abs("/favicon.png") },
      image: abs("/og/og-default.jpg"),
      slogan: "Premiumfilm som abonnemang.",
      description:
        "Fenny är en svensk filmbyrå som levererar premiumfilm som abonnemang (Pulse) och skräddarsydda produktioner (Prime). Strategi, regi, foto, klipp, grade och versionering — i marknadens takt. Första klippet inom 10–14 dagar.",
      foundingDate: `${FENNY_FACTS.foundedYear}`,
      inLanguage: LOCALE,
      areaServed: { "@type": "Country", name: "Sweden" },
      serviceArea: { "@type": "Country", name: "Sweden" },
      priceRange: FENNY_FACTS.priceRangeIndicator,
      knowsLanguage: ["sv", "en"],
      knowsAbout: [
        "Filmproduktion",
        "Brand film",
        "Reklamfilm",
        "Dokumentärfilm",
        "Företagsfilm",
        "Sociala medier-film",
        "Video marketing",
        "Content as a service",
        "Employer branding-film",
        "Rekryteringsfilm",
        "Kundcase-film",
        "Filmabonnemang",
        "Heritage-film och kunskapsarv",
        "Generationsskifte och successionsdokumentation",
        "Prisstatistik för filmproduktion",
      ],
      makesOffer: [
        {
          "@type": "Offer",
          name: "Pulse — filmabonnemang",
          url: abs("/pulse"),
          priceCurrency: "SEK",
          category: "Subscription",
        },
        {
          "@type": "Offer",
          name: "Prime — skräddarsydd produktion",
          url: abs("/prime"),
          priceCurrency: "SEK",
          category: "Project",
        },
      ],
      address: postalAddress,
      ...(telephone && { telephone }),
      email,
      openingHoursSpecification: OFFICE_HOURS,
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "sales",
          areaServed: "SE",
          availableLanguage: ["Swedish", "English"],
          url: abs("/kontakt"),
          ...(telephone && { telephone }),
          email,
        },
      ],
      sameAs: [...FENNY_PROFILES, ...extraSameAs, ...wikidataUrl(), ...placeProfileUrls()],
      ...(MEMBERSHIPS.length > 0 && {
        memberOf: MEMBERSHIPS.map((m) => ({
          "@type": "Organization",
          name: m.name,
          url: m.url,
        })),
      }),
      ...(AWARDS.length > 0 && { award: AWARDS }),
      publishingPrinciples: abs("/redaktionell-policy"),
      mainEntityOfPage: abs("/om-fenny"),
      subjectOf: [
        { "@type": "WebPage", url: abs("/redaktionen") },
        // Öppna dataset knutna till entiteten — stärker GEO/citerbarhet.
        {
          "@type": "Dataset",
          name: "Prisindex filmproduktion Sverige 2026",
          url: abs("/branschstudier/prisindex-2026"),
          license: "https://creativecommons.org/licenses/by/4.0/",
        },
        {
          "@type": "Dataset",
          name: "Formatbenchmark filmproduktion Sverige 2026",
          url: abs("/branschstudier/formatbenchmark-2026"),
          license: "https://creativecommons.org/licenses/by/4.0/",
        },
        {
          "@type": "Dataset",
          name: "Filmproduktionsbarometern 2026",
          url: abs("/branschstudier/filmproduktionsbarometern"),
          license: "https://creativecommons.org/licenses/by/4.0/",
        },
      ],
      // Fenny är ett varumärke som drivs av Norrhavet Group AB — den
      // juridiska identiteten (orgnr) ligger därför på moderbolaget.
      parentOrganization: {
        "@type": "Organization",
        "@id": "https://norrhavet.com/#organization",
        name: "Norrhavet Group",
        legalName: PARENT_LEGAL_NAME,
        url: "https://norrhavet.com",
        sameAs: PARENT_PROFILES,
        taxID: PARENT_ORG_NUMBER,
        vatID: `SE${PARENT_ORG_NUMBER.replace("-", "")}01`,
        identifier: [
          {
            "@type": "PropertyValue",
            propertyID: "Organisationsnummer (Bolagsverket)",
            value: PARENT_ORG_NUMBER,
          },
        ],
      },
      // Juridisk identitet — utelämnas helt tills värdena är bekräftade.
      ...(organizationIdentifiers().length > 0 && {
        identifier: organizationIdentifiers(),
      }),
      ...(ORG_NUMBER ? { taxID: ORG_NUMBER } : {}),
      ...(VAT_ID ? { vatID: VAT_ID } : {}),
      // Omdömen: emitteras bara när verifierade omdömen finns publicerade.
      ...(aggregateRatingValue() && {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: aggregateRatingValue()!.value,
          reviewCount: aggregateRatingValue()!.count,
          bestRating: 5,
          worstRating: 1,
        },
      }),
      ...(CUSTOMER_REVIEWS.length > 0 && {
        review: CUSTOMER_REVIEWS.slice(0, 10).map((r) => ({
          "@type": "Review",
          author: { "@type": "Person", name: r.author },
          datePublished: r.date,
          reviewBody: r.body,
          ...(r.rating && {
            reviewRating: {
              "@type": "Rating",
              ratingValue: r.rating,
              bestRating: 5,
              worstRating: 1,
            },
          }),
          itemReviewed: { "@id": ORG_ID },
        })),
      }),

    },


    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      url: BASE_URL,
      name: BRAND,
      inLanguage: LOCALE,
      publisher: { "@id": ORG_ID },
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${BASE_URL}/kunskap?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
  ],
  };
};

/**
 * Samlat Service-nod för hela sajten (startsidan). Beskriver hela
 * erbjudandet — film — för sökmotorer och svarsmotorer.
 */
export const siteServiceGraph = (nap?: BusinessNap) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${BASE_URL}/#service`,
  name: "Filmproduktion",
  description:
    "Fenny producerar film för varumärken och organisationer: reklamfilm, företagsfilm, dokumentär, employer branding, studio och livesändning samt efterbearbetning.",
  url: BASE_URL,
  provider: { "@id": ORG_ID },
  serviceType: [
    "Filmproduktion",
    "Reklamfilm",
    "Företagsfilm",
    "Dokumentärfilm",
    "Employer branding-film",
    "Studio och livesändning",
    "Efterbearbetning och postproduktion",
  ],
  areaServed: { "@type": "Country", name: "Sweden" },
  availableChannel: {
    "@type": "ServiceChannel",
    serviceUrl: abs("/kontakt"),
    availableLanguage: ["sv", "en"],
    ...(nap?.phone?.trim() && { servicePhone: { "@type": "ContactPoint", telephone: nap.phone.trim() } }),
  },
  inLanguage: LOCALE,
});

export const breadcrumbs = (
  trail: { name: string; path: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((t, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: t.name,
    item: abs(t.path),
  })),
});

export const faqPage = (items: { q: string; a: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  inLanguage: LOCALE,
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});

// QAPage — för svars-sidor med EN huvudfråga (vad kostar X, hur lång ska X).
// Bättre fit än FAQPage när hela sidan är ett svar. Boostar AI Overviews
// och voice answers eftersom Google läser acceptedAnswer som direkt-svar.
export const qaPage = (input: {
  question: string;
  answer: string;
  path: string;
  authorName?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "QAPage",
  inLanguage: LOCALE,
  mainEntity: {
    "@type": "Question",
    name: input.question,
    text: input.question,
    url: abs(input.path),
    answerCount: 1,
    acceptedAnswer: {
      "@type": "Answer",
      text: input.answer,
      url: abs(input.path),
      ...(input.authorName && {
        author: { "@type": "Organization", name: input.authorName },
      }),
    },
  },
});

// Speakable — markerar vilka delar av sidan som ska läsas upp av
// Google Assistant / voice search. Pekar på CSS-selektorer eller XPaths.
export const speakable = (cssSelectors: string[]) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  speakable: {
    "@type": "SpeakableSpecification",
    cssSelector: cssSelectors,
  },
});

/**
 * AEO + GEO-block för en enskild sida.
 *
 * Ett WebPage-nod som ger:
 *  - AEO: speakable (h1, .lede, [data-quick-answer]) + primaryQuestion/answer
 *    så svarsmotorer hittar det citerbara kortsvaret.
 *  - GEO: isPartOf/about/publisher-koppling till entitetsgrafen, inLanguage,
 *    dateModified, licens och citation-format som LLM:er kan återge.
 *
 * Ska finnas på ALLA indexerbara sidor — kompletterar (ersätter inte)
 * sidans Service-/Article-/FAQ-schema.
 */
export const aeoGeo = (input: {
  path: string;
  name?: string;
  description?: string;
  /** Sidans huvudfråga + citerbart kortsvar (AEO). */
  question?: string;
  answer?: string;
  /** Extra speakable-selektorer utöver standarduppsättningen. */
  speakableSelectors?: string[];
  dateModified?: string;
  inLanguage?: string;
}) => {
  const url = abs(input.path);
  const lang = input.inLanguage ?? (input.path.startsWith("/en") ? "en" : LOCALE);
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    ...(input.name && { name: input.name }),
    ...(input.description && { description: input.description }),
    inLanguage: lang,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
    ...(input.dateModified && { dateModified: input.dateModified }),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [
        "h1",
        ".lede",
        "[data-quick-answer]",
        "[data-speakable]",
        ...(input.speakableSelectors ?? []),
      ],
    },
    ...(input.question &&
      input.answer && {
        mainEntity: {
          "@type": "Question",
          name: input.question,
          text: input.question,
          answerCount: 1,
          acceptedAnswer: {
            "@type": "Answer",
            text: input.answer,
            url,
            author: { "@id": ORG_ID },
          },
        },
      }),
    license: "https://creativecommons.org/licenses/by/4.0/",
    citation: `${BRAND} (${url})`,
    potentialAction: {
      "@type": "ReadAction",
      target: [url],
    },
  };
};

// Koordinater för de orter vi har lokala sidor för. Används i LocalBusiness
// så Google kan koppla sidan till rätt geografi i lokal sökning.
export const CITY_GEO: Record<string, { lat: number; lng: number }> = {
  stockholm: { lat: 59.3293, lng: 18.0686 },
  solna: { lat: 59.36, lng: 18.0 },
  sundbyberg: { lat: 59.3617, lng: 17.9714 },
  goteborg: { lat: 57.7089, lng: 11.9746 },
  göteborg: { lat: 57.7089, lng: 11.9746 },
  malmo: { lat: 55.605, lng: 13.0038 },
  malmö: { lat: 55.605, lng: 13.0038 },
  uppsala: { lat: 59.8586, lng: 17.6389 },
  vasteras: { lat: 59.6099, lng: 16.5448 },
  västerås: { lat: 59.6099, lng: 16.5448 },
  linkoping: { lat: 58.4109, lng: 15.6216 },
  linköping: { lat: 58.4109, lng: 15.6216 },
  norrkoping: { lat: 58.5877, lng: 16.1924 },
  norrköping: { lat: 58.5877, lng: 16.1924 },
  orebro: { lat: 59.2741, lng: 15.2066 },
  örebro: { lat: 59.2741, lng: 15.2066 },
  helsingborg: { lat: 56.0465, lng: 12.6945 },
  lund: { lat: 55.7047, lng: 13.191 },
  jonkoping: { lat: 57.7826, lng: 14.1618 },
  jönköping: { lat: 57.7826, lng: 14.1618 },
  boras: { lat: 57.721, lng: 12.9401 },
  borås: { lat: 57.721, lng: 12.9401 },
  umea: { lat: 63.8258, lng: 20.263 },
  umeå: { lat: 63.8258, lng: 20.263 },
  lulea: { lat: 65.5848, lng: 22.1547 },
  luleå: { lat: 65.5848, lng: 22.1547 },
  sundsvall: { lat: 62.3908, lng: 17.3069 },
  gavle: { lat: 60.6749, lng: 17.1413 },
  gävle: { lat: 60.6749, lng: 17.1413 },
  eskilstuna: { lat: 59.3666, lng: 16.5077 },
  karlstad: { lat: 59.4022, lng: 13.5115 },
  vaxjo: { lat: 56.8777, lng: 14.8091 },
  växjö: { lat: 56.8777, lng: 14.8091 },
  halmstad: { lat: 56.6745, lng: 12.8568 },
};

export const cityGeo = (city?: string) => {
  if (!city) return undefined;
  const hit = CITY_GEO[city.toLowerCase()];
  return hit ? { "@type": "GeoCoordinates", latitude: hit.lat, longitude: hit.lng } : undefined;
};

/** Kontorstid — samma öppettider för alla lokala entiteter. */
export const OFFICE_HOURS = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "https://schema.org/Monday",
      "https://schema.org/Tuesday",
      "https://schema.org/Wednesday",
      "https://schema.org/Thursday",
      "https://schema.org/Friday",
    ],
    opens: "08:00",
    closes: "18:00",
  },
];

const reserveAction = (input: { name: string; url: string; resultName: string }) => ({
  "@type": "ReserveAction",
  name: input.name,
  target: {
    "@type": "EntryPoint",
    urlTemplate: input.url,
    inLanguage: LOCALE,
    actionPlatform: [
      "https://schema.org/DesktopWebPlatform",
      "https://schema.org/MobileWebPlatform",
    ],
  },
  result: { "@type": "Reservation", name: input.resultName },
});

// Ortsschema. LocalBusiness emitteras ENDAST för orter där Fenny har
// kontor (Stockholm, Västerås, Malmö — se src/content/locations.ts).
// Övriga orter får Service med areaServed och Organization som provider:
// det är sant, och undviker att Google läser sidorna som doorway pages
// med påhittad lokal etablering.
function slugifyCity(city: string): string {
  return city
    .toLowerCase()
    .replace(/å|ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export const localBusinessForCity = (input: {
  city: string;
  path: string;
  description: string;
  postalRegion?: string;
  /** Sida där besökaren faktiskt kan boka/begära offert. Default /kontakt. */
  bookingPath?: string;
  /** Bilder som visar verksamheten på orten — stärker lokala kort. */
  images?: { src: string; alt: string }[];
}) => {
  const geo = cityGeo(input.city);
  const bookingUrl = abs(input.bookingPath ?? "/kontakt");
  const office = OFFICES.find(
    (o) => o.city.toLowerCase() === input.city.toLowerCase(),
  );
  const hasOffice = Boolean(office);
  const images =
    input.images && input.images.length > 0
      ? input.images
          .slice(0, 8)
          .map((i) => (i.src.startsWith("http") ? i.src : abs(i.src)))
      : abs("/og/og-default.jpg");

  if (!hasOffice) {
    // Ingen fysisk närvaro på orten: vi emitterar ProfessionalService med
    // areaServed = orten och address/geo = närmaste faktiska bas (kontor).
    // Det ger lokal relevans utan att påstå en adress vi inte har.
    const presence = presenceFor(slugifyCity(input.city), input.city);
    const base = presence.base;
    return {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "@id": `${BASE_URL}${input.path}#service-${slugifyCity(input.city)}`,
      name: `${BRAND} — filmproduktion i ${input.city}`,
      serviceType: ["Filmproduktion", "Video production service"],
      provider: { "@id": ORG_ID },
      parentOrganization: { "@id": ORG_ID },
      url: abs(input.path),
      description: input.description,
      // Adressen pekar på basen som faktiskt utför produktionen.
      address: {
        "@type": "PostalAddress",
        addressLocality: base,
        addressCountry: "SE",
      },
      ...(cityGeo(base) ? { geo: cityGeo(base) } : {}),
      areaServed: [
        { "@type": "City", name: input.city },
        ...(input.postalRegion
          ? [{ "@type": "AdministrativeArea", name: input.postalRegion }]
          : []),
      ],
      serviceArea: { "@type": "City", name: input.city },
      openingHoursSpecification: OFFICE_HOURS,
      email: "hey@fenny.se",
      priceRange: "€€€",
      currenciesAccepted: "SEK",
      knowsLanguage: ["sv", "en"],
      availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: bookingUrl,
        availableLanguage: ["sv", "en"],
      },
      image: images,
      inLanguage: LOCALE,
      potentialAction: reserveAction({
        name: `Boka möte — ${input.city}`,
        url: bookingUrl,
        resultName: `Filmuppdrag i ${input.city}`,
      }),
    };
  }


  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": `${BASE_URL}/#localbusiness-${slugifyCity(input.city)}`,
    name: `${BRAND} — ${input.city}`,
    parentOrganization: { "@id": ORG_ID },
    url: abs(input.path),
    description: input.description,
    email: "hey@fenny.se",
    ...(office?.telephone ? { telephone: office.telephone } : {}),
    address: {
      "@type": "PostalAddress",
      ...(office?.postalAddress?.streetAddress
        ? { streetAddress: office.postalAddress.streetAddress }
        : {}),
      addressLocality: input.city,
      ...(office?.postalAddress?.postalCode
        ? { postalCode: office.postalAddress.postalCode }
        : {}),
      ...(input.postalRegion ? { addressRegion: input.postalRegion } : {}),
      addressCountry: "SE",
    },
    areaServed: [
      { "@type": "City", name: input.city },
      ...(input.postalRegion ? [{ "@type": "AdministrativeArea", name: input.postalRegion }] : []),
    ],
    serviceArea: { "@type": "City", name: input.city },
    ...(geo ? { geo } : {}),
    openingHoursSpecification: OFFICE_HOURS,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Bokning och offert",
      email: "hey@fenny.se",
      availableLanguage: ["sv", "en"],
      areaServed: "SE",
    },
    priceRange: "€€€",
    currenciesAccepted: "SEK",
    knowsLanguage: ["sv", "en"],
    image: images,
    inLanguage: LOCALE,
    potentialAction: reserveAction({
      name: `Boka möte — ${input.city}`,
      url: bookingUrl,
      resultName: `Filmuppdrag i ${input.city}`,
    }),
  };
};



export const serviceSchema = (input: {
  name: string;
  path: string;
  description: string;
  serviceType: string;
  priceCurrency?: string;
  priceRange?: string;
  aggregateRating?: {
    ratingValue: number; // 1–5
    reviewCount: number;
    bestRating?: number;
    worstRating?: number;
  };
  areaServed?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: input.name,
  serviceType: input.serviceType,
  description: input.description,
  url: abs(input.path),
  provider: { "@id": ORG_ID },
  areaServed: input.areaServed && input.areaServed.length > 0
    ? input.areaServed.map((a) => ({ "@type": "Place", name: a }))
    : { "@type": "Country", name: "Sweden" },
  ...(input.aggregateRating && {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: input.aggregateRating.ratingValue,
      reviewCount: input.aggregateRating.reviewCount,
      bestRating: input.aggregateRating.bestRating ?? 5,
      worstRating: input.aggregateRating.worstRating ?? 1,
    },
  }),
  ...(input.priceRange && {
    offers: {
      "@type": "Offer",
      priceCurrency: input.priceCurrency ?? "SEK",
      priceSpecification: { "@type": "PriceSpecification", priceCurrency: "SEK" },
      price: input.priceRange,
      url: abs(input.path),
    },
  }),
});

export const definedTermSet = (
  name: string,
  path: string,
  terms: { term: string; description: string }[],
) => ({
  "@context": "https://schema.org",
  "@type": "DefinedTermSet",
  name,
  url: abs(path),
  inLanguage: LOCALE,
  hasDefinedTerm: terms.map((t) => ({
    "@type": "DefinedTerm",
    name: t.term,
    description: t.description,
    inDefinedTermSet: abs(path),
  })),
});

// VideoObject — kritiskt för en filmbyrå. Använd på alla sidor med
// inbäddad film (hero, case, format-showcase). uploadDate måste vara
// ISO 8601. duration är ISO 8601-duration ("PT1M30S").
export const videoObject = (input: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
  embedUrl?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: input.name,
  description: input.description,
  thumbnailUrl: input.thumbnailUrl.startsWith("http")
    ? input.thumbnailUrl
    : abs(input.thumbnailUrl),
  uploadDate: input.uploadDate,
  ...(input.duration && { duration: input.duration }),
  ...(input.contentUrl && { contentUrl: input.contentUrl }),
  ...(input.embedUrl && { embedUrl: input.embedUrl }),
  publisher: { "@id": ORG_ID },
  inLanguage: LOCALE,
});

// VideoObject med transcript — AEO/GEO-boost: LLM:er och Google läser
// transcript som citerbar text och kan svara på frågor om filmens innehåll.
// `transcript` är ren text; `caption`/`hasPart` (kapitel) är valfria.
export const videoObjectWithTranscript = (input: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  transcript: string;
  duration?: string;
  contentUrl?: string;
  embedUrl?: string;
  chapters?: { name: string; startOffsetSec: number; url?: string }[];
}) => ({
  ...videoObject({
    name: input.name,
    description: input.description,
    thumbnailUrl: input.thumbnailUrl,
    uploadDate: input.uploadDate,
    duration: input.duration,
    contentUrl: input.contentUrl,
    embedUrl: input.embedUrl,
  }),
  transcript: input.transcript,
  ...(input.chapters && input.chapters.length > 0 && {
    hasPart: input.chapters.map((c) => ({
      "@type": "Clip",
      name: c.name,
      startOffset: c.startOffsetSec,
      ...(c.url && { url: c.url.startsWith("http") ? c.url : abs(c.url) }),
    })),
  }),
});

// CaseStudy — för /case/$slug. Rikare än creativeWork: kopplar klient,
// problem, lösning, mätbart resultat och inbäddad film. Driver E-E-A-T
// och blir citerbar i AI Overviews ("filmbyrå som levererade X resultat").
export const caseStudy = (input: {
  name: string;
  description: string;
  path: string;
  image?: string;
  clientName?: string;
  industry?: string;
  datePublished?: string;
  about?: string[];
  /** Mätbara resultat, t.ex. "3,2× fler visningar på LinkedIn". */
  results?: string[];
  video?: {
    name: string;
    description: string;
    thumbnailUrl: string;
    uploadDate: string;
    duration?: string;
    contentUrl?: string;
    embedUrl?: string;
  };
}) => ({
  "@context": "https://schema.org",
  "@type": ["CreativeWork", "CaseStudy"],
  name: input.name,
  description: input.description,
  url: abs(input.path),
  ...(input.image && {
    image: input.image.startsWith("http") ? input.image : abs(input.image),
  }),
  creator: { "@id": ORG_ID },
  ...(input.clientName && {
    about: {
      "@type": "Organization",
      name: input.clientName,
      ...(input.industry && { industry: input.industry }),
    },
  }),
  ...(input.about && input.about.length > 0 && { keywords: input.about }),
  ...(input.datePublished && { datePublished: input.datePublished }),
  ...(input.results && input.results.length > 0 && {
    abstract: input.results.join(" • "),
  }),
  ...(input.video && {
    video: videoObject({ ...input.video }),
  }),
  inLanguage: LOCALE,
  mainEntityOfPage: { "@type": "WebPage", "@id": abs(input.path) },
});


// Article — för /kunskap/*. Author som Person-entity, inte sträng.
export const article = (input: {
  headline: string;
  description: string;
  path: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  authorName: string;
  authorRole?: string;
  section?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: input.headline,
  description: input.description,
  url: abs(input.path),
  ...(input.image && {
    image: input.image.startsWith("http") ? input.image : abs(input.image),
  }),
  datePublished: input.datePublished,
  dateModified: input.dateModified ?? input.datePublished,
  author: {
    "@type": "Person",
    name: input.authorName,
    ...(input.authorRole && { jobTitle: input.authorRole }),
    worksFor: { "@id": ORG_ID },
  },
  publisher: { "@id": ORG_ID },
  inLanguage: LOCALE,
  ...(input.section && { articleSection: input.section }),
  mainEntityOfPage: { "@type": "WebPage", "@id": abs(input.path) },
});

// CreativeWork — för /case/$slug. Kopplar verket till Fenny som creator.
export const creativeWork = (input: {
  name: string;
  description: string;
  path: string;
  image?: string;
  clientName?: string;
  datePublished?: string;
  about?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "CreativeWork",
  name: input.name,
  description: input.description,
  url: abs(input.path),
  ...(input.image && {
    image: input.image.startsWith("http") ? input.image : abs(input.image),
  }),
  creator: { "@id": ORG_ID },
  ...(input.clientName && {
    sourceOrganization: { "@type": "Organization", name: input.clientName },
  }),
  ...(input.datePublished && { datePublished: input.datePublished }),
  ...(input.about && { about: input.about }),
  inLanguage: LOCALE,
});

// HowTo — för /guider/$slug med stegvis innehåll.
export const howTo = (input: {
  name: string;
  description: string;
  path: string;
  steps: { name: string; text: string }[];
  totalTime?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: input.name,
  description: input.description,
  url: abs(input.path),
  ...(input.totalTime && { totalTime: input.totalTime }),
  step: input.steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.text,
  })),
  inLanguage: LOCALE,
});

// Person — för byline (E-E-A-T). Använd på alla /kunskap/* artiklar.
export const person = (input: {
  name: string;
  jobTitle?: string;
  email?: string;
  path?: string;
  image?: string;
  sameAs?: string[];
  knowsAbout?: string[];
  description?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: input.name,
  ...(input.jobTitle && { jobTitle: input.jobTitle }),
  ...(input.path && { url: abs(input.path) }),
  ...(input.email && {
    email: `mailto:${input.email}`,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      email: input.email,
      availableLanguage: ["sv", "en"],
    },
  }),
  ...(input.image && {
    image: input.image.startsWith("http") ? input.image : abs(input.image),
  }),
  worksFor: { "@id": ORG_ID },
  ...(input.description && { description: input.description }),
  ...(input.knowsAbout && input.knowsAbout.length > 0 && {
    knowsAbout: input.knowsAbout,
  }),
  ...(input.sameAs && input.sameAs.length > 0 && { sameAs: input.sameAs }),
});

// Claim — citerbar siffra/påstående för AEO. ChatGPT/Perplexity/AIO
// plockar gärna `Claim` med tydlig `appearance` + `author` när de
// vill källhänvisa. Använd för konkreta benchmarks (priser, ledtider,
// produktionsvolym) som vi vill bli citerade på.
export const claim = (input: {
  text: string; // den citerbara meningen, t.ex. "Pulse-kunder har första klippet inom 10–14 dagar."
  path: string; // sidan där claimet visas
  datePublished?: string;
  about?: string; // ämnesbeskrivning, t.ex. "Filmproduktion i Sverige 2026"
}) => ({
  "@context": "https://schema.org",
  "@type": "Claim",
  text: input.text,
  url: abs(input.path),
  ...(input.datePublished && { datePublished: input.datePublished }),
  ...(input.about && { about: input.about }),
  appearance: { "@type": "WebPage", url: abs(input.path) },
  author: { "@id": ORG_ID },
});

// ItemList — för listsidor (case-grid, kunskap-index, stad×tjänst-leveranser).
// AI Overviews plockar gärna listor som direkta svar.
export const itemList = (input: {
  name: string;
  path: string;
  items: { name: string; path: string; description?: string }[];
}) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: input.name,
  url: abs(input.path),
  numberOfItems: input.items.length,
  itemListElement: input.items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: abs(it.path),
    name: it.name,
    ...(it.description && { description: it.description }),
  })),
});

// LocalBusiness komplett — för stadshubbar med kontorsadress.
// Innehåller geo, openingHours, priceRange, image, sameAs (GBP/maps).
export const localBusinessFull = (input: {
  city: string;
  path: string;
  description: string;
  streetAddress?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  telephone?: string;
  email?: string;
  openingHours?: string[]; // t.ex. ["Mo-Fr 09:00-17:00"]
  sameAs?: string[]; // GBP-länk, maps-länk, LinkedIn-profil för kontoret
  images?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": `${BASE_URL}/#localbusiness-${input.city.toLowerCase()}`,
  name: `${BRAND} — ${input.city}`,
  parentOrganization: { "@id": ORG_ID },
  url: abs(input.path),
  description: input.description,
  priceRange: "€€€",
  inLanguage: LOCALE,
  knowsLanguage: ["sv", "en"],
  ...(input.telephone && { telephone: input.telephone }),
  ...(input.email && { email: input.email }),
  ...(input.streetAddress && {
    address: {
      "@type": "PostalAddress",
      streetAddress: input.streetAddress,
      addressLocality: input.city,
      ...(input.postalCode && { postalCode: input.postalCode }),
      addressCountry: "SE",
    },
  }),
  ...(input.latitude != null && input.longitude != null && {
    geo: {
      "@type": "GeoCoordinates",
      latitude: input.latitude,
      longitude: input.longitude,
    },
  }),
  areaServed: { "@type": "City", name: input.city },
  serviceArea: { "@type": "City", name: input.city },
  ...(input.openingHours && { openingHoursSpecification: input.openingHours.map((h) => ({
    "@type": "OpeningHoursSpecification",
    description: h,
  })) }),
  image: (input.images && input.images.length > 0
    ? input.images
    : [abs("/og/og-default.jpg")]).map((i) => (i.startsWith("http") ? i : abs(i))),
  ...(input.sameAs && input.sameAs.length > 0 && { sameAs: input.sameAs }),
});

/**
 * ImageGallery med ImageObject-noder — gör bilderna citerbara för
 * Google Images, AI-sök och GEO-ytor. Alt-texten blir `caption`.
 */
export const imageGallery = (input: {
  name: string;
  path: string;
  description: string;
  images: { src: string; alt: string; w?: number; h?: number }[];
}) => ({
  "@context": "https://schema.org",
  "@type": "ImageGallery",
  name: input.name,
  description: input.description,
  url: abs(input.path),
  inLanguage: LOCALE,
  provider: { "@id": ORG_ID },
  associatedMedia: input.images.slice(0, 25).map((img) => ({
    "@type": "ImageObject",
    contentUrl: img.src.startsWith("http") ? img.src : abs(img.src),
    caption: img.alt,
    ...(img.w ? { width: img.w } : {}),
    ...(img.h ? { height: img.h } : {}),
    creator: { "@id": ORG_ID },
    copyrightNotice: `© ${BRAND}`,
    creditText: BRAND,
    acquireLicensePage: abs("/kontakt"),
  })),
});

/**
 * Google-kort för fotoverksamheten — LocalBusiness/ProfessionalService med
 * bildgalleri, kontaktperson (Fenny) och offert-action. Egen @id-rymd
 * (#fotobusiness-…) så den inte krockar med filmens stads-entiteter.
 */
export const photoBusiness = (input: {
  /** Ort, eller utelämna för den nationella fotoentiteten. */
  city?: string;
  path: string;
  description: string;
  region?: string;
  images: { src: string; alt: string }[];
  email?: string;
  contactName?: string;
  /** Körsida där bokning sker. Default: offertformuläret på sidan. */
  bookingPath?: string;
}) => {
  const key = input.city ? input.city.toLowerCase() : "sverige";
  const email = input.email ?? "hey@fenny.se";
  const url = abs(input.path);
  const geo = cityGeo(input.city);
  const bookingUrl = input.bookingPath ? abs(input.bookingPath) : `${url}#offert`;

  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": `${BASE_URL}/#fotobusiness-${key}`,
    name: input.city ? `${BRAND} Foto — ${input.city}` : `${BRAND} Foto`,
    alternateName: input.city ? `Fotograf ${input.city}` : "Fotograf för företag",
    parentOrganization: { "@id": ORG_ID },
    url,
    description: input.description,
    email,
    priceRange: "€€€",
    currenciesAccepted: "SEK",
    inLanguage: LOCALE,
    knowsLanguage: ["sv", "en"],
    address: {
      "@type": "PostalAddress",
      addressLocality: input.city ?? "Stockholm",
      ...(input.region ? { addressRegion: input.region } : {}),
      addressCountry: "SE",
    },
    areaServed: [
      ...(input.city ? [{ "@type": "City", name: input.city }] : []),
      ...(input.region ? [{ "@type": "AdministrativeArea", name: input.region }] : []),
      { "@type": "Country", name: "Sverige" },
    ],
    ...(input.city ? { serviceArea: { "@type": "City", name: input.city } } : {}),
    ...(geo ? { geo } : {}),
    openingHoursSpecification: OFFICE_HOURS,

    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Bokning och offert — foto",
      name: input.contactName ?? "Fenny",
      email,
      availableLanguage: ["sv", "en"],
      areaServed: "SE",
    },
    image: input.images
      .slice(0, 12)
      .map((i) => (i.src.startsWith("http") ? i.src : abs(i.src))),
    photo: input.images.slice(0, 12).map((i) => ({
      "@type": "ImageObject",
      contentUrl: i.src.startsWith("http") ? i.src : abs(i.src),
      caption: i.alt,
      creditText: `${BRAND} Foto`,
      creator: { "@id": ORG_ID },
      acquireLicensePage: abs("/kontakt"),
    })),
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: input.city ? `Fototjänster i ${input.city}` : "Fototjänster",
      itemListElement: [
        "Företagsporträtt",
        "Kampanjfoto",
        "Produktfoto",
        "Eventfoto",
        "Studiofotografering",
      ].map((n) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: n, serviceType: "Fotografering" },
        priceCurrency: "SEK",
        url,
      })),
    },
    potentialAction: reserveAction({
      name: input.city ? `Boka fotograf i ${input.city}` : "Begär fotooffert",
      url: bookingUrl,
      resultName: input.city ? `Fotouppdrag i ${input.city}` : "Fotouppdrag",
    }),

  };
};
